import { AfterViewInit, Component, Inject, OnDestroy } from '@angular/core';
import {
	MatDatepickerInputEvent,
	MatDatepickerInput,
	MatDatepickerToggle,
	MatDatepicker,
} from '@angular/material/datepicker';
import {
	MatDialogRef,
	MAT_DIALOG_DATA,
	MatDialogContent,
	MatDialogActions,
} from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UserProfile } from 'src/app/models/model';
import {
	ApiService,
	QueryParam,
	ApiLoadingKeys,
} from 'src/app/services/api.services';
import { AuthService } from 'src/app/services/auth.service';
import {
	BehaviorSubject,
	Observable,
	of,
	interval,
	debounce,
	mergeMap,
	Subscription,
	from,
	map,
} from 'rxjs';
import { Client } from '../../../models/client';
import { UserProfileService } from '../../../services/user-profile.service';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { LoadingIndicatorComponent } from '../../../components/loading-indicator/loading-indicator.component';
import { MultiSelectTypeaheadComponent } from '../../../components/multi-select-typeahead/multi-select-typeahead.component';
import { MatDivider } from '@angular/material/list';
import {
	MatFormField,
	MatLabel,
	MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { FormsModule } from '@angular/forms';

export type AlertReportFilterOptions =
	Components.Schemas.AlertReportFilterOptions;

export const emptyAlertReportFilters = (): AlertReportFilters => ({
	devices: [],
	alertTypes: [],
	deviceOwnerCompanies: [],
	firstName: [],
	lastName: [],
	clientIdn: [],
	utcStartDate: undefined,
	utcEndDate: undefined,
});

export type AlertReportFilters = {
	deviceOwnerCompanies?: { id: string; name: string }[];
	alertTypes?: { id: string; name: string }[];
	devices?: { id: string; serialNumber: string }[];
	properties?: { id: string; name: string }[];
	postalCodes?: { id: string; name: string }[];
	firstName?: Client[];
	lastName?: Client[];
	clientIdn?: Client[];
	utcStartDate?: Date;
	utcEndDate?: Date;
};

interface SearchConfig {
	type:
		| 'device'
		| 'deviceOwner'
		| 'alertType'
		| 'property'
		| 'postalCode'
		| 'firstName'
		| 'lastName'
		| 'clientIdn';
	searchEvents: BehaviorSubject<string>;
	subscription: Subscription;
	selectedItems: Set<any>;
	options: any[];
	apiCall: (params: QueryParam[]) => Promise<any>;
	mapResponse: (item: any) => any;
	updateFilters: (items: any[]) => void;
}

const ALERT_TYPE_OPTIONS = [
	{ id: '1', name: 'lowBattery' },
	{ id: '2', name: 'missingKey' },
	{ id: '3', name: 'keyNotDetected' },
	{ id: '4', name: 'keyDetected' },
];

@Component({
	selector: 'app-alert-report-dialog',
	templateUrl: './alert-report-dialog.component.html',
	styleUrls: ['./alert-report-dialog.component.scss', '../../common/main.scss'],
	imports: [
		MatIconButton,
		MatIcon,
		CdkScrollable,
		MatDialogContent,
		LoadingIndicatorComponent,
		MultiSelectTypeaheadComponent,
		MatDivider,
		MatFormField,
		MatLabel,
		MatInput,
		MatDatepickerInput,
		FormsModule,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		MatDialogActions,
		MatButton,
	],
})
export class AlertReportDialogComponent implements AfterViewInit, OnDestroy {
	alertReportFilters: AlertReportFilters = emptyAlertReportFilters();
	filterOptions?: AlertReportFilterOptions;

	apiLoadingKeys = ApiLoadingKeys;

	queryParams: QueryParam[] = [];

	deviceOwnerOptions?: { id?: string; name?: string }[] = [];
	deviceOwnerId: string[] = [];

	deviceOptions?: { id?: string; serialNumber?: string }[] = [];
	deviceId: string[] = [];

	alertTypeOptions?: { id?: string; name?: string }[] = [];
	alertTypeId: string[] = [];

	propertyOptions?: { id?: string; name?: string }[] = [];
	propertyId: string[] = [];

	postalCodeOptions?: { id?: string; name?: string }[] = [];
	postalCodeId: string[] = [];

	firstNames?: Client[] = [];
	firstName: string[] = [];

	lastNames?: Client[] = [];
	lastName: string[] = [];

	clientIdns?: Client[] = [];
	clientIdn: string[] = [];

	activeCompanyId = '';
	isSentrilock = false;
	isCareCompany = false;

	deviceMultiSelectSearchPlaceholder = $localize`:@@alertReportDialog-deviceMultiSelectSearchPlaceholder: Search Serial Numbers (optional)`;
	deviceOwnerMultiSelectSearchPlaceholder = $localize`:@@alertReportDialog-deviceOwnerMultiSelectSearchPlaceholder: Search Companies (optional)`;
	alertTypeMultiSelectSearchPlaceholder = $localize`:@@alertReportDialog-alertTypeMultiSelectSearchPlaceholder: Search Alert Types (optional)`;
	propertyNameMultiSelectSearchPlaceholder = $localize`:@@alertReportDialog-propertyNameMultiSelectSearchPlaceholder: Search Property Names (optional)`;
	postalCodesMultiSelectSearchPlaceholder = $localize`:@@alertReportDialog-postalCodesMultiSelectSearchPlaceholder: Search Postal Codes (optional)`;
	firstNameMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-firstNameMultiSelectSearchPlaceholder: Search Client First Names (optional)`;
	lastNameMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-lastNameMultiSelectSearchPlaceholder: Search Client Last Names (optional)`;
	clientIdnMultiSelectSearchPlaceholder = $localize`:@@inventoryReportDialog-clientIdnMultiSelectSearchPlaceholder: Search Client IDNs (optional)`;

	private readonly searchConfigs: { [key: string]: SearchConfig } = {
		device: {
			type: 'device',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.deviceOptions || [],
			apiCall: (params) => this.apiService.getAllDevices(params),
			mapResponse: (device) => ({
				id: device.device.id,
				serialNumber: device.device.serialNumber,
			}),
			updateFilters: (items) => {
				this.alertReportFilters.devices = [...items];
			},
		},
		deviceOwner: {
			type: 'deviceOwner',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.deviceOwnerOptions || [],
			apiCall: (params) => this.apiService.getCompanyFilterOptions(params),
			mapResponse: (company) => ({
				id: company.id,
				name: company.name,
			}),
			updateFilters: (items) => {
				this.alertReportFilters.deviceOwnerCompanies = [...items];
			},
		},
		alertType: {
			type: 'alertType',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: ALERT_TYPE_OPTIONS,
			apiCall: () => Promise.resolve(ALERT_TYPE_OPTIONS),
			mapResponse: (alertType) => alertType,
			updateFilters: (items) => {
				this.alertReportFilters.alertTypes = [...items];
			},
		},
		property: {
			type: 'property',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.propertyOptions || [],
			apiCall: (params) => this.apiService.getProperties(params),
			mapResponse: (property) => ({
				id: property.property.id,
				name: property.property.name,
			}),
			updateFilters: (items) => {
				this.alertReportFilters.properties = [...items];
			},
		},
		postalCode: {
			type: 'postalCode',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.postalCodeOptions || [],
			apiCall: (params) => this.apiService.getPostalFilterOptions(params),
			mapResponse: (postal) => ({
				id: postal.id,
				name: postal.name,
			}),
			updateFilters: (items) => {
				this.alertReportFilters.postalCodes = [...items];
			},
		},
		firstName: {
			type: 'firstName',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.firstNames || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.alertReportFilters.firstName = [...items];
			},
		},
		lastName: {
			type: 'lastName',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.lastNames || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.alertReportFilters.lastName = [...items];
			},
		},
		clientIdn: {
			type: 'clientIdn',
			searchEvents: new BehaviorSubject<string>(''),
			subscription: new Subscription(),
			selectedItems: new Set(),
			options: this.clientIdns || [],
			apiCall: (params) =>
				this.apiService.getClients(this.activeCompanyId, params),
			mapResponse: (client) => client,
			updateFilters: (items) => {
				this.alertReportFilters.clientIdn = [...items];
			},
		},
	};

	constructor(
		private readonly dialogRef: MatDialogRef<AlertReportDialogComponent>,
		private readonly apiService: ApiService,
		private readonly router: Router,
		private readonly authService: AuthService,
		private readonly userProfileService: UserProfileService,
		@Inject(MAT_DIALOG_DATA)
		public data?: {
			reportFilters: AlertReportFilters;
			filterOptions: AlertReportFilterOptions;
		}
	) {
		this.filterOptions = data?.filterOptions;
		this.alertReportFilters = data?.reportFilters ?? emptyAlertReportFilters();

		// Initialize alert type options immediately
		this.alertTypeOptions = ALERT_TYPE_OPTIONS;

		if (data?.reportFilters) {
			this.alertReportFilters.deviceOwnerCompanies?.forEach((deviceOwner) => {
				this.deviceOwnerId.push(deviceOwner.id);
			});
			this.alertReportFilters.devices?.forEach((device) => {
				this.deviceId.push(device.id);
			});
			this.alertReportFilters.alertTypes?.forEach((alertType) => {
				this.alertTypeId.push(alertType.id);
			});
			this.alertReportFilters.properties?.forEach((property) => {
				this.propertyId.push(property.id);
			});
			this.alertReportFilters.postalCodes?.forEach((postalCode) => {
				this.postalCodeId.push(postalCode.id);
			});
		}

		this.initializeData();
		Object.keys(this.searchConfigs).forEach((key) => this.setupSearch(key));
	}

	async ngAfterViewInit(): Promise<void> {
		const userProfile: UserProfile = await this.apiService.getUserProfile();
		this.isCareCompany =
			userProfile?.activeRoleDetails?.company?.companyType === 'care';
		this.activeCompanyId = userProfile?.activeRoleDetails?.company?.id ?? '';
		const sentrilockId = await this.apiService.getSentrilockCompanyId();
		this.isSentrilock = this.activeCompanyId === sentrilockId;
	}

	get hasFilters(): boolean {
		let hasFilters =
			JSON.stringify(this.alertReportFilters) !==
			JSON.stringify(emptyAlertReportFilters());

		return hasFilters;
	}

	get primaryButtonText(): string {
		return this.hasFilters
			? $localize`:@@alertReportDialog-generate:Generate`
			: $localize`:@@alertReportDialog-generateWithoutFilters:Generate without Filters`;
	}

	onSelectedDeviceOwnerEvent(deviceOwner: any): void {
		if (deviceOwner === undefined) {
			return;
		}
		this.alertReportFilters.deviceOwnerCompanies = [...deviceOwner];
	}

	onSelectedDeviceEvent(device: any): void {
		if (device === undefined) {
			return;
		}
		this.alertReportFilters.devices = [...device];
	}

	onSelectedAlertTypeEvent(alertType: any): void {
		if (alertType === undefined) {
			return;
		}
		this.alertReportFilters.alertTypes = [...alertType];
	}

	onSelectedPropertyEvent(property: any): void {
		if (property === undefined) {
			return;
		}
		this.alertReportFilters.properties = [...property];
	}

	onSelectedPostalCodeEvent(postalCode: any): void {
		if (postalCode === undefined) {
			return;
		}
		this.alertReportFilters.postalCodes = [...postalCode];
	}

	onDateValidStartDateChanged(event: MatDatepickerInputEvent<Date>): void {
		this.alertReportFilters.utcStartDate = event.value ?? undefined;
	}

	onDateValidEndDateChanged(event: MatDatepickerInputEvent<Date>): void {
		this.alertReportFilters.utcEndDate = event.value ?? undefined;
	}

	onCloseDialog(): void {
		this.dialogRef.close();
	}

	async onGenerateReport(): Promise<void> {
		const compiledFilters = this.compileFilters();

		this.router
			.navigateByUrl(`/reports/alert-report-table`, {
				state: {
					reportFilters: this.alertReportFilters,
					compiledFilters,
					filterOptions: this.filterOptions,
					hasFilters: this.hasFilters,
				},
			})
			.then(() => {
				this.dialogRef.close();
			});
	}

	compileFilters(): QueryParam[] {
		const queryParameters: QueryParam[] = [];

		if (
			this.alertReportFilters.deviceOwnerCompanies &&
			this.alertReportFilters.deviceOwnerCompanies.length > 0
		) {
			queryParameters.push({
				param: 'deviceOwnerCompanies',
				value: this.alertReportFilters.deviceOwnerCompanies.map((it) => it.id),
			});
		}

		if (
			this.alertReportFilters.alertTypes &&
			this.alertReportFilters.alertTypes.length > 0
		) {
			queryParameters.push({
				param: 'alertTypes',
				value: this.alertReportFilters.alertTypes.map((it) => it.id),
			});
		}

		if (
			this.alertReportFilters.devices &&
			this.alertReportFilters.devices.length > 0
		) {
			queryParameters.push({
				param: 'devices',
				value: this.alertReportFilters.devices.map((it) => it.id),
			});
		}

		if (
			this.alertReportFilters.properties &&
			this.alertReportFilters.properties.length > 0
		) {
			queryParameters.push({
				param: 'properties',
				value: this.alertReportFilters.properties.map((it) => it.id),
			});
		}

		if (
			this.alertReportFilters.postalCodes &&
			this.alertReportFilters.postalCodes.length > 0
		) {
			queryParameters.push({
				param: 'postalCodes',
				value: this.alertReportFilters.postalCodes.map((it) => it.id),
			});
		}

		if (
			this.alertReportFilters.firstName &&
			this.alertReportFilters.firstName.length > 0
		) {
			queryParameters.push({
				param: 'firstNames',
				value: this.alertReportFilters.firstName.map(
					(it) => it.firstName || ''
				),
			});
		}

		if (
			this.alertReportFilters.lastName &&
			this.alertReportFilters.lastName.length > 0
		) {
			queryParameters.push({
				param: 'lastNames',
				value: this.alertReportFilters.lastName.map((it) => it.lastName || ''),
			});
		}

		if (
			this.alertReportFilters.clientIdn &&
			this.alertReportFilters.clientIdn.length > 0
		) {
			queryParameters.push({
				param: 'clientIdns',
				value: this.alertReportFilters.clientIdn.map((it) => it.idn || ''),
			});
		}

		if (this.alertReportFilters.utcStartDate) {
			queryParameters.push({
				param: 'startDate',
				value: this.alertReportFilters.utcStartDate.toISOString().split('T')[0],
			});
		}

		if (this.alertReportFilters.utcEndDate) {
			queryParameters.push({
				param: 'endDate',
				value: this.alertReportFilters.utcEndDate.toISOString().split('T')[0],
			});
		}

		return queryParameters;
	}

	private initializeData(): void {
		this.filterOptions = this.data?.filterOptions;
		// Initialize deviceOptions with previous selections
		if (this.alertReportFilters.devices?.length) {
			this.alertReportFilters.devices.forEach((device) => {
				const deviceOption = {
					id: device.id,
					serialNumber: device.serialNumber,
				};
				this.deviceId.push(device.id);
				this.searchConfigs.device.selectedItems.add(deviceOption);
			});

			// Initialize deviceOptions with selected devices
			this.deviceOptions = Array.from(this.searchConfigs.device.selectedItems);
		}

		// Initialize deviceOwnerOptions with previous selections
		if (this.alertReportFilters.deviceOwnerCompanies?.length) {
			this.alertReportFilters.deviceOwnerCompanies.forEach((company) => {
				const companyOption = {
					id: company.id,
					name: company.name,
				};
				this.deviceOwnerId.push(company.id);
				this.searchConfigs.deviceOwner.selectedItems.add(companyOption);
			});
			// Initialize deviceOwnerOptions with selected companies
			this.deviceOwnerOptions = Array.from(
				this.searchConfigs.deviceOwner.selectedItems
			);
		}

		// Initialize property options
		if (this.alertReportFilters.properties?.length) {
			this.alertReportFilters.properties.forEach((property) => {
				const propertyOption = { propertyName: property.name };
				this.propertyId.push(property.name);
				this.searchConfigs.property.selectedItems.add(propertyOption);
			});
			this.propertyOptions = Array.from(
				this.searchConfigs.property.selectedItems
			);
		}

		// Initialize postal code options
		if (this.alertReportFilters.postalCodes?.length) {
			this.alertReportFilters.postalCodes.forEach((postal) => {
				const postalOption = { postalCode: postal.name };
				this.postalCodeId.push(postal.name);
				this.searchConfigs.postalCode.selectedItems.add(postalOption);
			});
			this.postalCodeOptions = Array.from(
				this.searchConfigs.postalCode.selectedItems
			);
		}
	}

	private setupSearch(type: string): void {
		const config = this.searchConfigs[type];
		const searchResults = config.searchEvents.asObservable().pipe(
			debounce(() => interval(300)),
			mergeMap((searchTerm) => this.handleSearch(searchTerm, config))
		);

		config.subscription = searchResults.subscribe();
	}

	private handleSearch(
		searchTerm: string,
		config: SearchConfig
	): Observable<any[]> {
		// Special case for alertType - always show all options
		if (config.type === 'alertType') {
			return of(ALERT_TYPE_OPTIONS);
		}

		if (['firstName', 'lastName', 'clientIdn'].includes(config.type)) {
			if (!searchTerm || searchTerm.length < 2) {
				switch (config.type) {
					case 'firstName':
						return of(this.firstNames || []);
					case 'lastName':
						return of(this.lastNames || []);
					case 'clientIdn':
						return of(this.clientIdns || []);
				}
			}
		} else {
			// All other filters: minimum 3 characters
			if (!searchTerm || searchTerm.length < 3) {
				switch (config.type) {
					case 'device':
						return of(this.deviceOptions || []);
					case 'deviceOwner':
						return of(this.deviceOwnerOptions || []);
					case 'property':
						return of(this.propertyOptions || []);
					case 'postalCode':
						return of(this.postalCodeOptions || []);
				}
			}
		}

		const queryParameters: QueryParam[] = [
			{ param: this.getSearchParamName(config), value: searchTerm },
			{ param: 'rowIndex', value: 0 },
			{ param: 'rowCount', value: 100 },
		];

		return from(config.apiCall(queryParameters)).pipe(
			map((results) => {
				if (!results?.length) {
					return Array.from(config.selectedItems);
				}

				const newOptions = results.map(config.mapResponse);
				const mergedOptions = this.mergeWithSelected(
					newOptions,
					config.selectedItems
				);

				switch (config.type) {
					case 'device':
						this.deviceOptions = mergedOptions;
						break;
					case 'deviceOwner':
						this.deviceOwnerOptions = mergedOptions;
						break;
					case 'alertType':
						this.alertTypeOptions = mergedOptions;
						break;
					case 'property':
						this.propertyOptions = mergedOptions;
						break;
					case 'postalCode':
						this.postalCodeOptions = mergedOptions;
						break;
					case 'firstName':
						this.firstNames = mergedOptions;
						break;
					case 'lastName':
						this.lastNames = mergedOptions;
						break;
					case 'clientIdn':
						this.clientIdns = mergedOptions;
						break;
				}

				return mergedOptions;
			})
		);
	}

	private getSearchParamName(config: SearchConfig): string {
		switch (config.type) {
			case 'device':
				return 'serialNumber';
			case 'deviceOwner':
				return 'companyName';
			case 'alertType':
				return 'name';
			case 'property':
				return 'propertyName';
			case 'postalCode':
				return 'postalCode';
			case 'firstName':
				return 'firstName';
			case 'lastName':
				return 'lastName';
			case 'clientIdn':
				return 'idn';
			default:
				return '';
		}
	}

	private mergeWithSelected(newOptions: any[], selectedItems: Set<any>): any[] {
		if (!newOptions) newOptions = [];
		if (!selectedItems) return newOptions;

		const newOptionIds = new Set(newOptions.map((opt) => opt.id));
		let mergedOptions = [...newOptions];

		selectedItems.forEach((selected) => {
			if (newOptionIds.has(selected.id)) {
				// if the selected item is already in the new options list, remove it from mergedOptions
				mergedOptions = mergedOptions.filter((opt) => opt.id !== selected.id);
			}
		});

		return mergedOptions;
	}

	onSearchInput(type: string, searchTerm: string): void {
		this.searchConfigs[type].searchEvents.next(searchTerm);
	}

	onSelectedItemsChanged(type: string, items: any): void {
		if (!items) return;

		const config = this.searchConfigs[type];
		config.selectedItems = new Set(items);
		config.updateFilters(items);
		config.searchEvents.next('');
	}

	ngOnDestroy(): void {
		Object.values(this.searchConfigs).forEach((config) => {
			if (config.subscription) {
				config.subscription.unsubscribe();
			}
		});
	}
}
